/**
 * Keep one microphone capture stream for the live-call page session.
 *
 * Every consumer used to call getUserMedia independently. Besides creating
 * duplicate capture tracks, browsers that scope permission to the current
 * capture session could show their permission prompt again on reconnect.
 */
let microphoneStream = null;
let microphoneStreamPromise = null;

function hasLiveAudioTrack(stream) {
    return !!(stream && stream.getAudioTracks().some((track) => track.readyState === 'live'));
}

export function getVideocallMicrophoneStream(debugLog = function () {}) {
    if (hasLiveAudioTrack(microphoneStream)) {
        debugLog('Reusing previously granted microphone stream');
        return Promise.resolve(microphoneStream);
    }

    if (microphoneStreamPromise) {
        debugLog('Reusing pending microphone access request');
        return microphoneStreamPromise;
    }

    debugLog('Requesting microphone access');
    microphoneStreamPromise = navigator.mediaDevices.getUserMedia({
        audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
        },
    }).then((stream) => {
        microphoneStream = stream;
        microphoneStreamPromise = null;

        stream.getAudioTracks().forEach((track) => {
            debugLog('Microphone track ready', {
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                settings: typeof track.getSettings === 'function' ? track.getSettings() : null,
            });
            track.addEventListener('mute', () => {
                debugLog('Microphone track muted by browser', { readyState: track.readyState });
            });
            track.addEventListener('unmute', () => {
                debugLog('Microphone track unmuted by browser', { readyState: track.readyState });
            });
            track.addEventListener('ended', () => {
                debugLog('Microphone track ended', {
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                });
                if (microphoneStream === stream && !hasLiveAudioTrack(stream)) {
                    microphoneStream = null;
                }
            }, { once: true });
        });

        return stream;
    }).catch((error) => {
        microphoneStreamPromise = null;
        microphoneStream = null;
        throw error;
    });

    return microphoneStreamPromise;
}

export function releaseVideocallMicrophoneStream() {
    if (microphoneStream) {
        microphoneStream.getTracks().forEach((track) => track.stop());
    }
    microphoneStream = null;
    microphoneStreamPromise = null;
}
